/**
 * MODULE: domain/pages/frontend/components/editor/CrmPreviewBlockShell.tsx
 * PURPOSE: Shared card chrome for live CRM preview blocks
 */

import type { ReactNode } from 'react';
import { Spinner } from 'flowbite-react';
import { HiExternalLink, HiRefresh } from 'react-icons/hi';

type Props = {
  testIdPrefix: string;
  selected: boolean;
  ringClass: string;
  borderClass: string;
  gradientClass: string;
  icon: ReactNode;
  badgeLabel: string;
  badgeClassName: string;
  title: string;
  meta: ReactNode;
  isLoading: boolean;
  isError: boolean;
  errorHint: string;
  isFetching: boolean;
  entityId: string;
  onRefresh: () => void;
  onOpenCrm: () => void;
  openLabel?: string;
  /** Anonymous public share — no CRM fetches or in-app navigation. */
  publicReadOnly?: boolean;
};

export function CrmPreviewBlockShell({
  testIdPrefix,
  selected,
  ringClass,
  borderClass,
  gradientClass,
  icon,
  badgeLabel,
  badgeClassName,
  title,
  meta,
  isLoading,
  isError,
  errorHint,
  isFetching,
  entityId,
  onRefresh,
  onOpenCrm,
  openLabel = 'Open in CRM',
  publicReadOnly = false,
}: Props) {
  return (
    <div
      className={`overflow-hidden rounded-xl border shadow-sm ${borderClass} bg-gradient-to-br ${gradientClass} ${
        selected ? `ring-2 ring-offset-2 dark:ring-offset-gray-950 ${ringClass}` : ''
      }`}
      data-testid={`${testIdPrefix}-block`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">{icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p
              className="truncate text-base font-semibold text-gray-900 dark:text-white"
              data-testid={`${testIdPrefix}-name`}
            >
              {title}
            </p>
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${badgeClassName}`}
            >
              {badgeLabel}
            </span>
          </div>
          {isLoading ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <Spinner size="sm" aria-label="Loading CRM data" />
              Loading live CRM data…
            </div>
          ) : isError ? (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{errorHint}</p>
          ) : (
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300" data-testid={`${testIdPrefix}-meta`}>
              {meta}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-inherit bg-white/70 px-4 py-2 dark:bg-gray-950/40">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {publicReadOnly ? 'Shared snapshot — sign in to Brazn for live CRM data' : 'Live snapshot from CRM'}
        </p>
        {!publicReadOnly ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="inline-flex items-center rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
              onClick={onRefresh}
              disabled={!entityId || isFetching}
              data-testid={`${testIdPrefix}-refresh`}
              aria-label="Refresh CRM data"
            >
              <HiRefresh className={`mr-1 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
              Refresh
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium hover:bg-white/80 dark:hover:bg-gray-800"
              onClick={onOpenCrm}
              disabled={!entityId}
              data-testid={`${testIdPrefix}-open-crm`}
            >
              <HiExternalLink className="mr-1 h-3.5 w-3.5" aria-hidden />
              {openLabel}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
